import { ApplyModes, BaseAPI, FormFieldAPIFactory, FormActionAPI, FormGroupAPIFactory } from 'lecom-api';
import { FormStore, FormActions } from 'form/reflux/form_store';
import { FormEvents } from 'form/events/form_event_handler';
import FormStoreItem from 'form/models/form_store_item';
import FormGroupItem from 'form/models/group/form_group_item';
import { getClassProperties } from 'utils/object_utils';
import { GridFormStore } from 'grid_form/reflux/grid_form_store';
import { ModalActions, ModalStore } from '../../realize_overrides/stores/modal_store.js';

var instances =  { };

/**
 * Classe FormAPI
 * @class FormAPI
 * @extends BaseAPI
 */
class FormAPI extends BaseAPI {
	static applyMode = ApplyModes.LAZY;

	/**
	 *
	 * @static
	 *
	 * @param {*} id
	 * @param {*} applyMode
	 */
	static form(id, applyMode = FormAPI.applyMode) {
		if (!instances[id])
			instances[id] = new FormAPI({id, applyMode});
		return instances[id];
	}

	/**
	 * @static
	 */
	static formProperties() {
		return getClassProperties(FormStoreItem)
			.filter((p) => ['id', 'fields', 'actions', 'errors', 'groups', 'readOnly'].indexOf(p) === -1);
	}

	/**
	 * @constructor
	 * @param {*} param0
	 */
	constructor({ id, storeId, applyMode }) {
		super({ id, storeId, applyMode, storeClass: FormStore });

		FormAPI.formProperties().forEach((formProperty) => {
			this[formProperty] = this._propertyAccessor.bind(this, formProperty);
		});

		this._fields = null;
		this._actions = null;
		this._groups = null;
		this._countModalOpened = 0;
	}

	/**
	 * Lista as APIs de campo para cada campo básico do Form (que não seja parte de um grid). O maior detalhamento sobre o uso desta propriedade é descrito abaixo.
	 *
	 * @param {*} fieldId
	 */
	fields(fieldId) {
		if (!this._fields) {
			this._fields = this._buildFieldsAPIs();
		}		

		if (typeof fieldId === 'number') {						
			return Object.keys(this._fields).map(field => this._fields[field]).find(field => field.fieldId() === fieldId);
		}

		return fieldId ? this._fields[fieldId] : Object.keys(this._fields).map(field => this._fields[field]);
	}

	grids(gridId){
		const grids = this.fields().filter(field => field.type() === "GRID");

		if (gridId !== undefined && gridId !== null)
			return grids.find(grid => grid.id === gridId);
		else
			return grids;
	}

	groups(groupId){
		if (!this._groups) {
			this._groups = this._buildGroupsAPIs();
		}

		if (typeof groupId === 'number') {									
			return Object.keys(this._groups).map(group => this._groups[group]).find(group => group.id === groupId);
		}

		return groupId ? this._groups[groupId] : Object.keys(this._groups).map(group => this._groups[group]);
	}

	allFields() {
		return this.fields()
			.map(f => f.type() === 'GRID' ? [f].concat(f.fields()) : f)
			.reduce((acc, f) => acc.concat(f), []);
	}

	/**
	 * Lista as APIs de ação para as ações relacionadas diretamente ao Form (que não estejam relacionadas a um campo). O maior detalhamento sobre o uso desta propriedade é descrito abaixo.
	 *
	 * @param {*} actionId
	 */
	actions(actionId) {
		if (!this._actions) {
			this._actions = this._buildActionsAPIs();
		}

		return actionId ? this._actions.find(action => action.id === actionId) : this._actions;
	}

	addCustomModal(objProps = {}){
		const modalId = 'custom-modal-api';
		const modalStore = ModalStore;
		const modalToOpenIds = modalStore.openedModalIds;
		const openedModal = modalToOpenIds.includes(modalId);

		if(openedModal != true){
			ModalActions.open({
				modalId: 'custom-modal-api',
				type: 'addCustomModal',
				title: objProps.title || '',
				description: objProps.description || '',
				showButtonClose: objProps.showButtonClose,
				buttons: objProps.buttons,
				dismissible: false
			});
		}

		return this;
	}

	showLoader(objProps = {}){
		const modalId = 'custom-modal-api';
		var modalStore = ModalStore;
		var modalToOpenId = modalStore.openedModalIds;
		var openedModal = modalToOpenId.includes(modalId);

		this._countModalOpened++;

		if(this._countModalOpened === 1){
			ModalActions.open({
				modalId: modalId,
				type: 'showLoader',
				description: objProps.description || Realize.i18n.t('modal.loading'),
				icon: objProps.icon,
				dismissible: false,
				isScroll: objProps.isScroll
			});
		}
	}

	hideLoader(objProps = {}){
		const modalId = 'custom-modal-api';
		var modalStore = ModalStore;
		var modalToOpenId = modalStore.openedModalIds;
		var openedModal = modalToOpenId.includes(modalId);

		this._countModalOpened = 0;

		if(openedModal !== false){
			ModalActions.close({
				modalId: modalId,
				isScroll: objProps.isScroll
			});
		}
		return this;
	}

	/**
	 *
	 */
	loadInitialValues() {
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;

		storeActions.loadInitialValues(this.storeId);
		return this._buildEventPromise(storeEvents.INITIAL_VALUES_LOAD_SUCCESS, storeEvents.INITIAL_VALUES_LOAD_ERROR);
	}

	/**
	 *
	 */
	loadDefinition() {
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;

		storeActions.loadFormDefinition(this.storeId);
		return this._buildEventPromise(storeEvents.DEFINITION_LOAD_SUCCESS, storeEvents.DEFINITION_LOAD_ERROR);
	}

	containsError(){
		let error = this.errors();
		let validateError = Object.keys(error);

		if(validateError.length > 0)
			return true;
		else
			return false;
	}

	errors(errors) {
		if(errors === undefined)
			return this.get('errors');

		const allFields = this.allFields();
		const allErrors = Object.assign({}, this.errors(), errors);

		for (let fieldId in allErrors) {
			const field = allFields.find(f => f.id === fieldId);

			if (field.type() === 'LABEL') {
				delete errors[field.id];
			}
			else {
				field.set({ updateUUID: 'updateUUID' });
			}
		}

		setTimeout(() => {
			Form.flagError(true).apply();
		},0);


		return this.set({ errors: errors });
	}

	readOnly(value) {
		if (value === undefined) {
			return this.get('readOnly');
		}

		this.allFields().map(async field => await field.readOnly(value));

		return this.set({ readOnly: value });
	}

	apply() {
		const storeActions = this.storeClass.listenables;
		const storesGrid = GridFormStore.store;

		return new Promise((resolve, reject) => {			
			for (let storeId in storesGrid) {								
				if (this.fields(storeId)) {
					this.fields(storeId).apply();
				}
			}

			storeActions.setState(this.storeId, resolve);
		});
	}

	/**
	 * @private
	 */
	_buildFieldsAPIs() {
		var fields = this.get('fields');
		var returnFields = { };

		for (let field in fields) {
			if (fields[field].component === 'gridform') {
				returnFields[field] = FormFieldAPIFactory.create(fields[field], {
					formId: this.id || fields[field].formId,
					storeId: this.storeId,
					applyMode: this.applyMode,
					type: fields[field].type
				});
			}
			else {
				returnFields[field] = FormFieldAPIFactory.create(fields[field], {
					formId: this.id || fields[field].formId,
					storeId: this.storeId,
					applyMode: this.applyMode
				});
			}
		}

		return returnFields;
	}

	/**
 	* @private
	*/
	_buildGroupsAPIs(){
		var groups = this.get('fieldsGroupings');
		var returnGroups = { };
		groups.forEach((group, i) => {
			returnGroups[group.identifier] = FormGroupAPIFactory.create(group, {
				formId: this.id,
				storeId: this.storeId,
				applyMode: this.applyMode
			});
		});

		return returnGroups;
	}

	/**
	* @private
	*/
	_buildActionsAPIs() {
		const actions = this.get('actions');
		return actions.map((action) => new FormActionAPI({
			id: action.id,
			formId: this.id || action.formId,
			storeId: this.storeId,
			applyMode: this.applyMode,
		}));
	}
};

export default FormAPI;
